import React from 'react'

interface MarkdownRendererProps {
  content: string
}

/**
 * A lightweight, dependency-free Markdown renderer that supports:
 * - Headings (# H1, ## H2, ### H3)
 * - Lists (* or - items)
 * - Inline bold (**text**)
 * - Links ([text](url))
 * - Horizontal rules (---)
 * - Paragraphs
 *
 * Applying unified design system styles via Tailwind CSS.
 */
export const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({ content }) => {
  const parseInline = (text: string): React.ReactNode[] => {
    const parts: React.ReactNode[] = []
    let keyIndex = 0

    // Match bold text (**text**) and Markdown links ([text](url))
    const regex = /(\*\*.*?\*\*|\[.*?\]\(.*?\))/g
    const splitText = text.split(regex)

    for (const part of splitText) {
      if (part.startsWith('**') && part.endsWith('**')) {
        parts.push(
          <strong key={keyIndex++} className="font-semibold text-foreground">
            {part.slice(2, -2)}
          </strong>
        )
      } else if (part.startsWith('[') && part.includes('](') && part.endsWith(')')) {
        const closeBracketIndex = part.indexOf('](')
        const linkText = part.slice(1, closeBracketIndex)
        const url = part.slice(closeBracketIndex + 2, -1)
        parts.push(
          <a
            key={keyIndex++}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#3b82f6] dark:text-[#60a5fa] hover:underline font-medium transition-colors"
          >
            {linkText}
          </a>
        )
      } else if (part) {
        parts.push(<span key={keyIndex++}>{part}</span>)
      }
    }

    return parts.length > 0 ? parts : [text]
  }

  const lines = content.split('\n')
  const elements: React.ReactNode[] = []
  let listItems: string[] = []
  let inList = false
  let keyIndex = 0

  const flushList = () => {
    if (listItems.length > 0) {
      elements.push(
        <ul key={`list-${keyIndex++}`} className="list-disc pl-6 mb-6 space-y-2 text-slate-600 dark:text-slate-300 leading-relaxed">
          {listItems.map((item, idx) => (
            <li key={idx} className="marker:text-slate-400 dark:marker:text-slate-500">
              {parseInline(item)}
            </li>
          ))}
        </ul>
      )
      listItems = []
      inList = false
    }
  }

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim()

    if (!line) {
      flushList()
      continue
    }

    // Horizontal Rule
    if (line === '---') {
      flushList()
      elements.push(<hr key={keyIndex++} className="my-8 border-slate-200 dark:border-slate-800" />)
      continue
    }

    // Headings
    if (line.startsWith('# ')) {
      flushList()
      elements.push(
        <h1 key={keyIndex++} className="text-3xl font-extrabold text-slate-900 dark:text-slate-50 tracking-tight mt-8 mb-6 first:mt-0">
          {parseInline(line.slice(2))}
        </h1>
      )
      continue
    }

    if (line.startsWith('## ')) {
      flushList()
      elements.push(
        <h2 key={keyIndex++} className="text-xl font-bold text-slate-800 dark:text-slate-100 tracking-tight mt-8 mb-4 border-b border-slate-200 dark:border-slate-800 pb-2">
          {parseInline(line.slice(3))}
        </h2>
      )
      continue
    }

    if (line.startsWith('### ')) {
      flushList()
      elements.push(
        <h3 key={keyIndex++} className="text-lg font-semibold text-slate-800 dark:text-slate-200 tracking-tight mt-6 mb-3">
          {parseInline(line.slice(4))}
        </h3>
      )
      continue
    }

    // List items
    if (line.startsWith('* ') || line.startsWith('- ')) {
      inList = true
      listItems.push(line.slice(2))
      continue
    }

    // Paragraph
    flushList()
    elements.push(
      <p key={keyIndex++} className="mb-4 text-slate-600 dark:text-slate-300 leading-relaxed text-[15px]">
        {parseInline(line)}
      </p>
    )
  }

  flushList()

  return <div className="markdown-content">{elements}</div>
}
export default MarkdownRenderer
